import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { useRadio } from "@/context/RadioContext";
import { Play, Pause, SkipForward, SkipBack, Shuffle, Radio as RadioIcon, MapPin, CircleDot, MessageSquare, ArrowRight, Music2, Bookmark } from "lucide-react";

const NBARS = 56;
function seededPeaks(seed) {
  let x = 7;
  const s = seed || "s";
  for (let i = 0; i < s.length; i++) x = (x * 31 + s.charCodeAt(i)) % 9973;
  const out = [];
  for (let i = 0; i < NBARS; i++) {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    const env = Math.sin((i / NBARS) * Math.PI);
    out.push(0.22 + ((x % 1000) / 1000) * 0.78 * (0.5 + 0.5 * env));
  }
  return out;
}

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

export default function Radio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    tracks, idx, setIdx, track, started, playing, cur, dur, progress, loading, loaded,
    resume, resumeIndex, ensureLoaded, startRadio, toggle, next, prev, reshuffle, seekRatio,
  } = useRadio();
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  useEffect(() => {
    if (user?.has_browse_access) api.get("/saved").then((r) => setSavedIds(new Set(r.data.ids || []))).catch(() => {});
  }, [user]);

  const peaks = useMemo(() => seededPeaks(track?.song_id || track?.title || "haus"), [track?.song_id, track?.title]);

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekRatio((e.clientX - rect.left) / rect.width);
  };

  const goProfile = () => track && navigate(`/profile/${track.user_id}`);
  const goMessage = () => {
    if (!track) return;
    if (user?.has_browse_access) navigate(`/messages?to=${track.user_id}`);
    else navigate("/paywall");
  };

  const toggleSave = async () => {
    if (!track) return;
    if (!user?.has_browse_access) { navigate("/paywall"); return; }
    const uid = track.user_id;
    const isSaved = savedIds.has(uid);
    try {
      if (isSaved) {
        await api.delete(`/saved/${uid}`);
        setSavedIds((prev) => { const n = new Set(prev); n.delete(uid); return n; });
        toast("Removed from your playlist");
      } else {
        await api.post(`/saved/${uid}`);
        setSavedIds((prev) => new Set(prev).add(uid));
        toast.success(`Saved ${track.name} to your playlist`);
      }
    } catch (e) { toast.error(errMessage(e)); }
  };

  const isSaved = track && savedIds.has(track.user_id);

  const playedIdx = Math.round((progress / 100) * NBARS);
  const avatar = track && mediaUrl(track.avatar);

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[1000px] mx-auto px-5 md:px-10 pt-24 pb-28 md:pb-20">
        <div className="flex items-center gap-3 mb-2">
          <RadioIcon size={18} className="text-gold" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Haus Radio</span>
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase text-cream leading-none">A continuous mix<br />of the Haus</h1>
        <p className="text-ash mt-4 max-w-md text-sm">One tap, endless discovery. Real songs by real members — each card slides in as it plays.</p>

        {loading || !loaded ? (
          <div className="mt-16 font-mono text-xs uppercase tracking-[0.3em] text-ash animate-pulse">Tuning in…</div>
        ) : tracks.length === 0 ? (
          <div className="mt-16 border border-dashed border-line p-12 text-center text-ash">
            <Music2 size={32} className="mx-auto mb-3 text-line" />
            <p className="text-sm">Nothing on air yet. Songs join the radio when members opt them in from their HausCard.</p>
          </div>
        ) : (
          <div className="mt-10">
            {!started && (
              <div className="flex flex-wrap items-center gap-4">
                {resumeIndex >= 0 && (
                  <button
                    onClick={() => startRadio("resume")}
                    data-testid="radio-resume-btn"
                    className="group inline-flex items-center gap-3 bg-gold text-ink font-bold uppercase tracking-widest text-sm px-8 py-5 hover:bg-gold-hi transition-colors"
                  >
                    <Play size={18} /> Resume “{resume.title}”
                    <span className="font-mono text-[11px] font-normal opacity-70">{fmt(resume.time)}</span>
                  </button>
                )}
                <button
                  onClick={() => startRadio("shuffle")}
                  data-testid="radio-start-btn"
                  className={`group inline-flex items-center gap-3 font-bold uppercase tracking-widest text-sm px-8 py-5 transition-colors ${
                    resumeIndex >= 0
                      ? "border border-line text-cream hover:border-gold hover:text-gold"
                      : "bg-gold text-ink hover:bg-gold-hi"
                  }`}
                >
                  <Shuffle size={18} /> Shuffle &amp; play
                  <span className="font-mono text-[11px] font-normal opacity-70">{tracks.length} tracks</span>
                </button>
              </div>
            )}

            <AnimatePresence mode="wait">
              {started && track && (
                <motion.div
                  key={idx}
                  data-testid="radio-now-playing"
                  initial={{ opacity: 0, x: 64 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -64 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="grid md:grid-cols-[240px_1fr] gap-px bg-line border border-line"
                >
                  {/* Cover / avatar */}
                  <button onClick={goProfile} data-testid="radio-open-card" className="bg-ink aspect-square md:aspect-auto relative overflow-hidden group">
                    {avatar
                      ? <img src={avatar} alt={track.name} className="w-full h-full object-cover media-grayscale" />
                      : <div className="w-full h-full flex items-center justify-center text-line"><Music2 size={48} /></div>}
                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-wider text-gold bg-ink/70 px-2 py-1">On air</span>
                  </button>

                  {/* Details + controls */}
                  <div className="bg-surface p-6 md:p-8 flex flex-col gap-5">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-wide text-ash mb-1">{(track.roles || []).slice(0, 2).join(" · ") || "Writer"}</p>
                      <h2 data-testid="radio-track-title" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-cream leading-tight">{track.title}</h2>
                      <button onClick={goProfile} data-testid="radio-artist-name" className="font-heading text-lg text-gold hover:underline text-left">{track.name}</button>
                    </div>

                    <div className="flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-wider text-ash">
                      {track.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-gold" />{track.location}</span>}
                      {track.open_to_collaborate && <span className="inline-flex items-center gap-1.5"><CircleDot size={12} className="text-gold" />Open to collab</span>}
                    </div>

                    {/* Waveform progress */}
                    <div className="flex items-center gap-4 mt-1">
                      <div className="relative h-10 flex-1 flex items-center gap-[2px] cursor-pointer" onClick={seek} data-testid="radio-scrub">
                        {peaks.map((p, i) => (
                          <div key={i} className={`flex-1 rounded-[1px] transition-[height,background-color] duration-150 ${i < playedIdx ? "bg-gold" : "bg-line"}`} style={{ height: `${Math.round(p * 100)}%` }} />
                        ))}
                      </div>
                      <span className="font-mono text-[11px] text-ash shrink-0 w-[72px] text-right">{fmt(cur)} / {fmt(dur)}</span>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-3">
                      <button onClick={prev} data-testid="radio-prev-btn" aria-label="Previous" className="w-11 h-11 flex items-center justify-center border border-line text-cream hover:border-gold hover:text-gold transition-colors"><SkipBack size={18} /></button>
                      <button onClick={toggle} data-testid="radio-play-toggle" aria-label={playing ? "Pause" : "Play"} className="w-14 h-14 flex items-center justify-center bg-gold text-ink hover:bg-gold-hi transition-colors">
                        {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
                      </button>
                      <button onClick={next} data-testid="radio-next-btn" aria-label="Next" className="w-11 h-11 flex items-center justify-center border border-line text-cream hover:border-gold hover:text-gold transition-colors"><SkipForward size={18} /></button>
                      <button onClick={reshuffle} data-testid="radio-shuffle-btn" aria-label="Reshuffle" className="w-11 h-11 flex items-center justify-center border border-line text-cream hover:border-gold hover:text-gold transition-colors"><Shuffle size={17} /></button>
                      <button
                        onClick={toggleSave}
                        data-testid="radio-save-btn"
                        aria-label={isSaved ? "Remove from playlist" : "Save to playlist"}
                        className={`w-11 h-11 flex items-center justify-center border transition-colors ${isSaved ? "border-gold bg-gold text-ink" : "border-line text-cream hover:border-gold hover:text-gold"}`}
                      >
                        <Bookmark size={17} fill={isSaved ? "currentColor" : "none"} />
                      </button>
                      <div className="flex-1" />
                      <button onClick={goMessage} data-testid="radio-message-btn" className="hidden sm:inline-flex items-center gap-2 border border-gold text-gold font-mono text-[11px] uppercase tracking-widest px-4 py-3 hover:bg-gold hover:text-ink transition-colors"><MessageSquare size={13} /> Message</button>
                    </div>

                    <button onClick={goProfile} data-testid="radio-view-card-btn" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ash hover:text-gold transition-colors">
                      View full HausCard <ArrowRight size={13} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Up next */}
            {started && tracks.length > 1 && (
              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ash mb-3">Up next</p>
                <div className="flex flex-col divide-y divide-line/60 border border-line">
                  {[1, 2, 3].map((o) => {
                    const t = tracks[(idx + o) % tracks.length];
                    if (!t) return null;
                    return (
                      <button
                        key={o}
                        onClick={() => setIdx((idx + o) % tracks.length)}
                        data-testid={`radio-upnext-${o}`}
                        className="flex items-center gap-3 p-3 bg-surface hover:bg-ink transition-colors text-left"
                      >
                        <div className="w-10 h-10 bg-ink border border-line overflow-hidden shrink-0">
                          {mediaUrl(t.avatar) ? <img src={mediaUrl(t.avatar)} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-heading text-sm font-bold text-cream truncate">{t.title}</p>
                          <p className="font-mono text-[10px] uppercase tracking-wide text-ash truncate">{t.name}</p>
                        </div>
                        <Play size={14} className="text-ash shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
